import { FunctionComponent, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    useGetConditionLinksQuery,
    useGetImportedConditionLinksQuery,
} from '@/api/v2/conditionLinks';
import { useAuthentication } from '@/hooks/useAuthentication';
import { ConditionLink } from '@/models/conditionLink';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type ConditionLinkAutocompleteProps = {
    value: string[];
    onChange: (conditionLinkIds: string[]) => void;
    label?: string;
};

const SortableChip = ({
    id,
    label,
    onDelete,
}: {
    id: string;
    label: string;
    onDelete: () => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Chip
                label={label}
                onDelete={onDelete}
                color='primary'
                sx={{ cursor: 'grab' }}
            />
        </Box>
    );
};

export const ConditionLinkAutocomplete: FunctionComponent<
    ConditionLinkAutocompleteProps
> = ({ value, onChange, label = 'Regelwerke auswählen' }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: conditionLinks = [] } = useGetConditionLinksQuery({
        liftOperatorId,
    });
    const { data: importedConditionLinks = [] } =
        useGetImportedConditionLinksQuery({
            liftOperatorId,
        });

    const allConditionLinks = useMemo(
        () => [...conditionLinks, ...importedConditionLinks],
        [conditionLinks, importedConditionLinks]
    );

    const selectedConditionLinks = useMemo(
        () =>
            (value ?? [])
                .map((id) => allConditionLinks.find((cl) => cl.id === id))
                .filter((cl): cl is ConditionLink => cl != null),
        [allConditionLinks, value]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = value.indexOf(String(active.id));
        const newIndex = value.indexOf(String(over.id));
        onChange(arrayMove(value, oldIndex, newIndex));
    };

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            <Autocomplete
                fullWidth
                multiple
                options={allConditionLinks}
                getOptionLabel={(option: ConditionLink) => option.name || ''}
                value={selectedConditionLinks}
                onChange={(_, newValue) => {
                    onChange(newValue.map((v) => v.id));
                }}
                renderInput={(params) => (
                    <TextField {...params} label={label} />
                )}
                renderTags={() => null}
            />
            {selectedConditionLinks.length > 0 && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={value}
                        strategy={verticalListSortingStrategy}
                    >
                        <Typography variant='caption' sx={{ mt: 2 }}>
                            Angewendete Regelwerke:
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {selectedConditionLinks.map((cl) => (
                                <SortableChip
                                    key={cl.id}
                                    id={cl.id}
                                    label={cl.name}
                                    onDelete={() =>
                                        onChange(
                                            value.filter((id) => id !== cl.id)
                                        )
                                    }
                                />
                            ))}
                        </Stack>
                    </SortableContext>
                </DndContext>
            )}
        </Box>
    );
};
